/**
 * W-020 — Content items list. Stories: US-A03.
 *
 * Filtered by subject, grouped by type, drag-to-reorder, video status column.
 * Ordering is per (subject, type) — the app lists each segment separately
 * (M-031's SegmentedControl), so reordering only makes sense inside a segment.
 */
import { CopyOutlined } from '@ant-design/icons'
import { CreateButton, DeleteButton, EditButton, List, useTable } from '@refinedev/antd'
import { useCan, useCreate, useInvalidate, useNotification } from '@refinedev/core'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Select, Space, Switch, Tabs, Tooltip, Typography } from 'antd'
import { useState } from 'react'
import { ModelInsights, OrderableTable, StatusTag } from '@/components'
import { recordAudit } from '@/lib/audit'
import { persistOrder } from '@/lib/order'
import { supabaseClient } from '@/lib/supabase'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { ContentItem, ContentType, Subject } from '@/types'
import { CONTENT_TYPES } from '@/types'
import { useSubjectOptions } from './useSubjectOptions'

interface ContentWatchStats {
  content_id: string
  watch_hours: number
  learners: number
  completion_rate: number
}

const decimal = (value: number, locale: string) =>
  new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'fr-FR', { maximumFractionDigits: 1 }).format(
    value,
  )

export const ContentItemListPage = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const [subjectId, setSubjectId] = useState<string | undefined>(undefined)
  const [type, setType] = useState<ContentType>('lesson')
  const { subjects, isLoading: subjectsLoading } = useSubjectOptions()
  const { open } = useNotification()
  const invalidate = useInvalidate()
  const { data: canEdit } = useCan({ resource: 'content_items', action: 'edit' })
  const { mutate: create } = useCreate()

  const { tableProps, tableQueryResult } = useTable<ContentItem>({
    resource: 'content_items',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 100 },
    filters: {
      permanent: [
        { field: 'type', operator: 'eq', value: type },
        ...(subjectId ? [{ field: 'subject_id', operator: 'eq' as const, value: subjectId }] : []),
      ],
    },
  })

  const rows = [...(tableProps.dataSource ?? [])]
  const rowIds = rows.map(row => row.id)

  const watchStatsQuery = useQuery({
    queryKey: ['content-watch-stats', rowIds],
    queryFn: async () => {
      const { data, error } = await supabaseClient.rpc('admin_content_watch_stats', {
        p_content_ids: rowIds,
      })
      if (error) throw error
      return (data ?? []) as ContentWatchStats[]
    },
    enabled: rowIds.length > 0,
  })
  const watchStatsByContentId = new Map(
    (watchStatsQuery.data ?? []).map(stat => [stat.content_id, stat]),
  )

  const handleReorder = (ordered: ContentItem[]) => {
    void persistOrder('content_items', ordered)
      .then(() => tableQueryResult.refetch())
      .catch((error: unknown) => {
        console.error('[content_items] reorder failed', error)
        void tableQueryResult.refetch()
      })
  }

  /** US-A03 AC6 — every field except the media is copied. */
  const duplicate = (record: ContentItem) => {
    create(
      {
        resource: 'content_items',
        values: {
          subject_id: record.subject_id,
          type: record.type,
          title_ar: `${record.title_ar} (نسخة)`,
          title_fr: `${record.title_fr} (copie)`,
          description_ar: record.description_ar,
          description_fr: record.description_fr,
          sort_order: rows.length,
          is_free: record.is_free,
          // Media is deliberately not copied: a Bunny video belongs to exactly
          // one content item, and a shared GUID would break the webhook mapping.
          video_id: null,
          video_status: 'none',
          pdf_path: null,
          is_published: false,
        },
      },
      {
        onSuccess: () => {
          void recordAudit({
            action: 'content.duplicate',
            target: 'content_items',
            targetId: record.id,
          })
          void invalidate({ resource: 'content_items', invalidates: ['list'] })
          open?.({
            type: 'success',
            message: translate('admin.screens.content.duplicatedTitle'),
            description: translate('admin.screens.content.duplicatedBody'),
          })
        },
      },
    )
  }

  return (
    <List
      title={translate('admin.pages.content')}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.content.subtitle'),
      }}
      headerButtons={<CreateButton disabled={!subjectId} />}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="content" />
        <div className="admin-filter-bar">
          <Select<string>
            style={{ minWidth: 320 }}
            placeholder={translate('admin.screens.content.chooseSubject')}
            allowClear
            loading={subjectsLoading}
            value={subjectId}
            onChange={setSubjectId}
            optionFilterProp="label"
            showSearch
            options={subjects.map((subject: Subject) => ({
              value: subject.id,
              label: `${subject.name_fr} — ${subject.name_ar}`,
            }))}
          />
        </div>

        {!subjectId ? (
          <Alert
            type="info"
            showIcon
            message={translate('admin.screens.content.reorderHint')}
            description={translate('admin.screens.content.reorderHintDetail')}
          />
        ) : null}

        <Tabs
          activeKey={type}
          onChange={key => setType(key as ContentType)}
          items={CONTENT_TYPES.map(value => ({
            key: value,
            label: translate(`admin.contentTypes.${value}Plural`),
          }))}
        />

        <div className="admin-filter-summary">
          <Typography.Text type="secondary">
            {subjectId
              ? translate('admin.screens.content.rowCount', { count: rows.length })
              : translate('admin.screens.content.selectSubjectToManage')}
          </Typography.Text>
          {subjectId ? (
            <Typography.Text type="secondary">
              {translate('admin.common.dragToReorder')}
            </Typography.Text>
          ) : null}
        </div>

        <OrderableTable<ContentItem>
          {...tableProps}
          dataSource={rows}
          scroll={{ x: 1300 }}
          onReorder={handleReorder}
          reorderDisabled={!canEdit?.can || !subjectId}
          columns={[
            {
              title: translate('admin.columns.titleAr'),
              dataIndex: 'title_ar',
              render: (value: string) => <span dir="rtl">{value}</span>,
            },
            { title: translate('admin.columns.titleFr'), dataIndex: 'title_fr' },
            {
              title: translate('admin.columns.video'),
              dataIndex: 'video_status',
              render: (value: ContentItem['video_status'], record: ContentItem) => (
                <StatusTag kind="video" value={value} reason={record.video_error} />
              ),
            },
            {
              title: translate('admin.columns.pdf'),
              dataIndex: 'pdf_path',
              render: (value: string | null) => (
                <Typography.Text type={value ? undefined : 'secondary'}>
                  {value ? translate('admin.common.yes') : '—'}
                </Typography.Text>
              ),
            },
            {
              title: translate('admin.columns.freePreview'),
              dataIndex: 'is_free',
              render: (value: boolean) => <Switch size="small" checked={value} disabled />,
            },
            {
              title: translate('admin.columns.state'),
              dataIndex: 'is_published',
              render: (value: boolean) => <StatusTag kind="published" value={value} />,
            },
            {
              title: translate('admin.columns.watchHours'),
              dataIndex: 'id',
              key: 'watch_hours',
              render: (_: unknown, record: ContentItem) => {
                const stat = watchStatsByContentId.get(record.id)
                if (watchStatsQuery.isLoading)
                  return <Typography.Text type="secondary">—</Typography.Text>
                return (
                  <Typography.Text>
                    {translate('admin.screens.content.hoursValue', {
                      value: decimal(stat?.watch_hours ?? 0, locale),
                    })}
                  </Typography.Text>
                )
              },
            },
            {
              title: translate('admin.columns.completionRate'),
              dataIndex: 'id',
              key: 'completion_rate',
              render: (_: unknown, record: ContentItem) => {
                const stat = watchStatsByContentId.get(record.id)
                if (watchStatsQuery.isLoading)
                  return <Typography.Text type="secondary">—</Typography.Text>
                if (!stat || stat.learners === 0) {
                  return (
                    <Typography.Text type="secondary">
                      {translate('admin.screens.content.noLearners')}
                    </Typography.Text>
                  )
                }
                return <Typography.Text>{decimal(stat.completion_rate, locale)}%</Typography.Text>
              },
            },
            {
              title: translate('admin.columns.actions'),
              dataIndex: 'id',
              render: (_: unknown, record: ContentItem) => (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <Tooltip title={translate('admin.screens.content.duplicateTooltip')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      aria-label={translate('admin.screens.content.duplicateTooltip')}
                      onClick={() => duplicate(record)}
                    />
                  </Tooltip>
                  <DeleteButton
                    hideText
                    size="small"
                    recordItemId={record.id}
                    confirmTitle={translate('admin.screens.content.deleteConfirm')}
                    confirmOkText={translate('common.delete')}
                    confirmCancelText={translate('common.cancel')}
                    onSuccess={() =>
                      void recordAudit({
                        action: 'content.delete',
                        target: 'content_items',
                        targetId: record.id,
                        before: { title_fr: record.title_fr, type: record.type },
                      })
                    }
                  />
                </Space>
              ),
            },
          ]}
        />
      </Space>
    </List>
  )
}
